import {queryCollection, getDocument} from '@/services/FirestoreService';
import {getCollection as getFilterCollection} from '@/lib/filters/filters.js';
import {FilterService} from '@/services/FilterService';
import {
  getGroupForBuilding,
  getGroupForIndustrialProject,
  getGroupForMultiFamilyProject,
  getGroupForAcquisitionProject,
  getGroupForComp,
} from '@/lib/filters/groups.js';
import {createMarkerEventHandlers} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {MapService} from '@/services/MapService';
import {baseActions, baseMutations, baseState, baseGetters} from '@/store/baseCollections';

import {wrapError} from '@northpoint-development/labs-common-core';
import {NEW_BUILDING_ID} from '@/lib/symbols';
import {stampMillis} from '@/lib/ReusableFunctions/Date';

/**
 * @typedef {import('vuex').Commit} Commit
 * @typedef {import('vuex').Dispatch} Dispatch
 * @typedef {import('vuex').ActionContext} ActionContext
 *
 * Retrieved buildings and projects from Salesforce.
 * @typedef {object} NorthPointResults
 * @property {object[]} industrialBuildings The NorthPoint Industrial Buildings retrieved from Salesforce.
 * @property {object[]} industrialProjects The Industrial Projects retrieved from Salesforce.
 * @property {object[]} multiFamilyProjects The Multi-Family Projects retrieved from Salesforce.
 *
 * @typedef {object} FirestoreResults
 * @property {object[]} dataCenterBuildings
 * @property {object[]} salesforceData
 * @property {object[]} multiFamilyBuildings
 * @property {object[]} competitiveBuildings
 * @property {object[]} competitiveBuildingComps
 *
 * @typedef {object} CompetitiveBuildingResult
 * @property {object[]} competitiveBuildings
 * @property {object[]} competitiveBuildingComps
 *
 * @typedef {Record<string, Array>} FieldSuggestions
 *
 * @typedef {object} MarkerEvent
 * @property {string} event
 * @property {Function} callback
 */

const firestoreCollectionNames = {
  npIndBldg: 'salesforce-industrial-buildings',
  npMFProj: 'salesforce-multifamily-projects',
  multiFamilyAcquisitionProject: 'salesforce-mf-acquisition-projects',
  npIndProj: 'salesforce-industrial-projects',
  npAcqProj: 'salesforce-acquisition-projects',
  compMF: 'beyondMaps-multifamily',
  compDC: 'beyondMaps-datacenter',
  projectDc: 'salesforce-datacenter-projects',
};

/** @type {string[]} */
const collectionNames = [
  'industrialBuildings',
  'industrialProjects',
  'multiFamilyBuildings', // competitive buildings
  'multiFamilyProjects', // northpoint projects
  'multiFamilyAcquisitionProjects',
  'acquisitionProjects',
  'dataCenterBuildings',
  'dataCenterProjects',
  'competitiveBuildings',
  'competitiveBuildingComps',
  'competitivePortfolios',
  'overlays',
  'features',
  // Tax Features need to be in their own collection for easily turning them on and off
  'taxFeatures',
];

/**
 * @type {Record<string, string>}
 * Maps a Firestore collection name to a local filter collection name.
 */
export const collectionNameByFirestoreCollection = {
  'salesforce-industrial-buildings': 'industrialBuildings',
  'salesforce-industrial-projects': 'industrialProjects',
  'beyondMaps-multifamily': 'multiFamilyBuildings',
  'salesforce-multifamily-projects': 'multiFamilyProjects',
  'salesforce-mf-acquisition-projects': 'multiFamilyAcquisitionProjects',
  'salesforce-acquisition-projects': 'acquisitionProjects',
  'beyondMaps-datacenter': 'dataCenterBuildings',
  'salesforce-datacenter-projects': 'dataCenterProjects',
  'beyondMaps-properties': 'competitiveBuildings',
  'beyondMaps-portfolios': 'competitivePortfolios',
  'beyondMaps-monday-items': 'mondayItems',
};

/**
 * @type {Record<string, string>}
 * Maps a local filter collection name to its Firestore collection name.
 */
export const firestoreCollectionByCollectionName = Object.fromEntries(
  Object.entries(collectionNameByFirestoreCollection).map(([firestore, local]) => [local, firestore]),
);

/**
 * Per-collection `asOf` watermarks from the last successful `/markers` response. A key's presence
 * marks the collection as polled. Exported for tests.
 * @type {Map<string, string>}
 */
export const markerWatermarks = new Map();

/**
 * An in-flight tick blocks new ones, but a tick older than this (e.g. black-holed by a laptop
 * sleep) is treated as dead and superseded; its late results are discarded.
 */
export const MARKER_POLL_ABANDON_MS = 15 * 60 * 1000;

/** Epoch ms when the in-flight poll tick started; 0 when idle. */
let markerPollStartedAt = 0;

/** Identifies the current tick so an abandoned one can detect it lost ownership. */
let markerPollTick = 0;

/**
 * Per-collection decoration of fetched slim markers, shared by the loaders and pollMarkerUpdates
 * so polled markers are indistinguishable from loaded ones. Each decorator mutates its markers in
 * place and returns the items eligible for insertion, which may be a filtered subset.
 * `competitiveBuildings` lives in decorateCompetitiveIndustrial instead because it also derives
 * the comps collection.
 */
const slimMarkerDecorators = {
  multiFamilyProjects(slimMarkers, {dispatch, getters}) {
    const salesforceCollection = firestoreCollectionNames.npMFProj;
    const entrataCollection = 'entrata-multifamily-properties';
    const filterCollection = 'multiFamilyProjects';
    slimMarkers.forEach((project) => {
      const isEntrata = project.source === 'entrata';
      const firestoreCollection = isEntrata ? entrataCollection : salesforceCollection;
      project.type = 'multiFamilyProject';
      project.group = getGroupForMultiFamilyProject(project);
      const icon = `${getters.colorFromProjectGroup(project.group)}-Multi-Family`;
      project.markerData = {
        type: isEntrata ? 'entrata' : 'salesforce',
        id: project.id,
        name: project.name,
        latLng: {lat: project.location?.latitude, lng: project.location?.longitude},
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            const fullProject = await getDocument(firestoreCollection, project.id);
            dispatch('infowindow/createProjectTab',
              {subject: {
                ...project,
                ...fullProject,
                type: project.type,
                group: project.group,
                markerData: project.markerData,
                firestoreCollection,
                filterCollection,
              }},
              {root: true},
            );
          },
        }],
      };
    });
    return slimMarkers;
  },
  industrialProjects(slimMarkers, {dispatch, getters}) {
    const firestoreCollection = firestoreCollectionNames.npIndProj;
    const filterCollection = 'industrialProjects';
    slimMarkers.forEach((project) => {
      project.type = 'industrialProject';
      project.group = getGroupForIndustrialProject(project);
      const icon = `${getters.colorFromProjectGroup(project.group)}-Circle`;
      project.markerData = {
        type: 'salesforce',
        id: project.id,
        name: project.name,
        latLng: {lat: project.location?.latitude, lng: project.location?.longitude},
        icon,
      };
      /** @type {MarkerEvent} */
      const clickEvent = {
        event: 'click',
        callback: async () => {
          const fullProject = await getDocument(firestoreCollection, project.id);
          dispatch('infowindow/createProjectTab',
            {subject: {
              ...project,
              ...fullProject,
              type: project.type,
              group: project.group,
              markerData: project.markerData,
              firestoreCollection,
              filterCollection,
            }},
            {root: true},
          );
        },
      };
      project.markerData.events = [clickEvent];
    });
    return slimMarkers;
  },
  multiFamilyAcquisitionProjects(slimMarkers, {dispatch, getters}) {
    const firestoreCollection = firestoreCollectionNames.multiFamilyAcquisitionProject;
    const filterCollection = 'multiFamilyAcquisitionProjects';
    slimMarkers.forEach((project) => {
      project.type = 'multiFamilyAcquisitionProject';
      project.group = getGroupForAcquisitionProject(project);
      const icon = `${getters.colorFromProjectGroup(project.group)}-Multi-Family`;
      project.markerData = {
        type: 'salesforce',
        id: project.id,
        name: project.name,
        latLng: {lat: project.location?.latitude, lng: project.location?.longitude},
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            const fullProject = await getDocument(firestoreCollection, project.id);
            dispatch('infowindow/createProjectTab',
              {subject: {
                ...project,
                ...fullProject,
                type: project.type,
                group: project.group,
                markerData: project.markerData,
                firestoreCollection,
                filterCollection,
              }},
              {root: true},
            );
          },
        }],
      };
    });
    return slimMarkers;
  },
  dataCenterProjects(slimMarkers, {dispatch, getters}) {
    const firestoreCollection = firestoreCollectionNames.projectDc;
    const filterCollection = 'dataCenterProjects';
    slimMarkers.forEach((project) => {
      project.type = 'dataCenterProject';
      // we can use the same group getting function because the stages match
      project.group = getGroupForIndustrialProject(project);
      const icon = `${getters.colorFromProjectGroup(project.group)}-DataCenterProject`;
      project.markerData = {
        type: 'salesforce',
        id: project.id,
        name: project.name,
        latLng: {lat: project.location?.latitude, lng: project.location?.longitude},
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            const fullProject = await getDocument(firestoreCollection, project.id);
            dispatch('infowindow/createProjectTab',
              {subject: {
                ...project,
                ...fullProject,
                type: project.type,
                group: project.group,
                markerData: project.markerData,
                firestoreCollection,
                filterCollection,
              }},
              {root: true},
            );
          },
        }],
      };
    });
    return slimMarkers;
  },
  acquisitionProjects(slimMarkers, {dispatch, getters}) {
    const firestoreCollection = firestoreCollectionNames.npAcqProj;
    const filterCollection = 'acquisitionProjects';
    slimMarkers.forEach((project) => {
      project.type = 'acquisitionProject';
      project.group = getGroupForAcquisitionProject(project);
      const icon = `${getters.colorFromProjectGroup(project.group)}-Acquisition`;
      project.markerData = {
        type: 'salesforce',
        id: project.id,
        name: project.name,
        latLng: {lat: project.location?.latitude, lng: project.location?.longitude},
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            const fullProject = await getDocument(firestoreCollection, project.id);
            dispatch('infowindow/createProjectTab',
              {subject: {
                ...project,
                ...fullProject,
                type: project.type,
                group: project.group,
                markerData: project.markerData,
                firestoreCollection,
                filterCollection,
              }},
              {root: true},
            );
          },
        }],
      };
    });
    return slimMarkers;
  },
  industrialBuildings(slimMarkers, {dispatch, getters}) {
    const firestoreCollection = firestoreCollectionNames.npIndBldg;
    const filterCollection = 'industrialBuildings';
    slimMarkers.forEach((building) => {
      try {
        building.group = getGroupForBuilding(building);
      } catch (error) {
        console.error(`Failed to get group for building ${building.id}: ${error.message}`);
        return;
      }
      const icon = `${getters.colorFromBuildingGroup(building.group)}-Compass-Up`;
      building.markerData = {
        type: 'salesforce',
        id: building.id,
        name: building.name,
        latLng: {lat: building.location?.latitude, lng: building.location?.longitude},
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            const fullBuilding = await getDocument(firestoreCollection, building.id);
            dispatch('infowindow/createIndustrialBuildingTab',
              {subject: {...building, ...fullBuilding, firestoreCollection, filterCollection}},
              {root: true},
            );
          },
        }],
      };
    });
    // A building whose group could not be derived has no markerData and cannot render.
    return slimMarkers.filter((building) => building.markerData);
  },
  multiFamilyBuildings(slimMarkers) {
    const firestoreCollection = firestoreCollectionNames.compMF;
    const filterCollection = 'multiFamilyBuildings';
    slimMarkers.forEach((building) => {
      building.markerData = {
        type: 'multiFamilyBuilding',
        id: building.id,
        name: building.name,
        latLng: {lat: building.location.latitude, lng: building.location.longitude},
        icon: 'Purple-Balloon',
        events: createMarkerEventHandlers(
          filterCollection,
          'createMultiFamilyBuildingTab',
          firestoreCollection,
          building.id,
        ),
      };
    });
    return slimMarkers;
  },
  dataCenterBuildings(slimMarkers) {
    const firestoreCollection = firestoreCollectionNames.compDC;
    const filterCollection = 'dataCenterBuildings';
    slimMarkers.forEach((building) => {
      building.markerData = {
        type: 'dataCenterBuilding',
        id: building.id,
        name: building.name,
        latLng: {lat: building.location.latitude, lng: building.location.longitude},
        icon: 'Yellow-Balloon',
        events: createMarkerEventHandlers(
          filterCollection,
          'createDataCenterBuildingTab',
          firestoreCollection,
          building.id,
        ),
      };
    });
    return slimMarkers;
  },
  competitivePortfolios(slimMarkers) {
    const firestoreCollection = 'beyondMaps-portfolios';
    const filterCollection = 'competitivePortfolios';
    slimMarkers.forEach((portfolio) => {
      if (!portfolio.location
        || portfolio.location.latitude === undefined
        || portfolio.location.longitude === undefined) {
        console.error(`Portfolio ${portfolio.id} is missing location data, cannot create marker`, portfolio);
        return;
      }
      portfolio.markerData = {
        type: 'competitivePortfolio',
        id: portfolio.id,
        name: portfolio.name,
        latLng: {lat: portfolio.location.latitude, lng: portfolio.location.longitude},
        icon: portfolio.generatedByOm ? 'Darker-Blue-Portfolio' : 'Blue-Portfolio-Flat',
        events: createMarkerEventHandlers(
          filterCollection,
          'createCompetitivePortfolioTab',
          firestoreCollection,
          portfolio.id,
        ),
      };
    });
    return slimMarkers.filter((p) => p.markerData); // filter out portfolios with missing location data
  },
};

/**
 * A building's slim doc restates its entire comp set, so the returned comps are complete for the
 * given buildings.
 * @return {{buildings: object[], comps: object[]}}
 */
function decorateCompetitiveIndustrial(slimBuildings, {dispatch, getters}) {
  const firestoreCollection = 'beyondMaps-properties';
  const compFilterCollection = 'competitiveBuildingComps';
  const competitiveBuildingFilterCollection = 'competitiveBuildings';
  const allComps = [];
  slimBuildings.forEach((building) => {
    if (
      !building.location
      || building.location.latitude === undefined
      || building.location.longitude === undefined
    ) {
      console.error(`Building ${building.id} is missing location data, cannot create comp markers`, building);
      return;
    }
    const ring = MapService.createCircle(
      building.location.latitude,
      building.location.longitude,
      building.comps?.length ?? 0,
    );
    building.comps?.forEach((comp, compIndex) => {
      const parentId = building.id;
      // Index-based like createMarkerDataForComp, so a save doesn't shuffle ring positions.
      const point = ring[compIndex];
      const compColor = getters.colorFromCompGroup(getGroupForComp(comp));
      const flatColor = compColor === 'Cyan' ? 'LightBlue' : compColor;
      const icon = comp.generatedByOm
        ? `${compColor}-Balloon`
        : `${flatColor}-Balloon-Flat`;
      const compMarkerData = {
        type: 'comp',
        id: comp.id,
        name: comp.name,
        // Actions such as merge act on the Comp's parent record; the comp picker keys a comp on it too.
        parent: {id: parentId, name: building.name},
        latLng: point,
        icon,
        events: [{
          event: 'click',
          callback: async () => {
            // includeIds: the InfoWindow content needs the doc's canonical id.
            const parent = await getDocument(firestoreCollection, parentId, true);
            // A comp marker can outlive its parent doc or its own comp entry (deleted between polls).
            if (!parent) {
              console.error(`Cannot open comp '${comp.id}': parent property '${parentId}' not found.`);
              return;
            }
            const fullComp = parent.comps?.find((c) => c.id === comp.id);
            if (!fullComp) {
              console.error(`Cannot open comp '${comp.id}': not found on parent property '${parentId}'.`);
              return;
            }
            dispatch('infowindow/createCompTab',
              {subject: {
                ...fullComp,
                parent,
                markerData: compMarkerData,
                firestoreCollection,
                filterCollection: compFilterCollection,
              }},
              {root: true},
            );
          },
        }],
      };
      allComps.push({
        ...comp,
        group: getGroupForComp(comp),
        parent: building,
        markerData: compMarkerData,
      });
    });
    const icon = building.generatedByOm ? 'Darker-Blue-Balloon' : 'Blue-Balloon-Flat';
    building.markerData = {
      type: 'competitiveBuilding',
      id: building.id,
      name: building.name,
      latLng: {lat: building.location.latitude, lng: building.location.longitude},
      icon,
      events: createMarkerEventHandlers(
        competitiveBuildingFilterCollection,
        'createCompetitiveBuildingTab',
        firestoreCollection,
        building.id,
      ),
    };
  });
  // A building missing location data has no markerData and cannot render.
  return {buildings: slimBuildings.filter((building) => building.markerData), comps: allComps};
}

/**
 * Items that exist only locally while a user is mid-creation: the NEW_BUILDING_ID building and
 * its derived comps. The backend cannot know them, so a full response — which deletes by absence —
 * must carry them forward.
 */
function localOnlyItems(collection) {
  return collection.getSource().filter((item) =>
    item.id === NEW_BUILDING_ID || item.parent?.id === NEW_BUILDING_ID);
}

/**
 * When a marker last changed: the later of `editedOn` and `syncedAt` (which also moves on
 * formula-only Salesforce changes); null when it has neither.
 */
function changedAtMillis(marker) {
  const stamps = [stampMillis(marker?.editedOn), stampMillis({timestamp: marker?.syncedAt})]
    .filter((millis) => millis !== null);
  return stamps.length ? Math.max(...stamps) : null;
}

/**
 * Reusing the collection's existing item skips re-decoration and keeps client state like comp ring
 * positions. A doc is reused when its stamp is unchanged (the backend looks back past `since`, and
 * windows overlap after a failed watermark advance) or the local copy is newer — a lagging backend
 * instance can serve a version older than a save made this session, which would revert it until the
 * next tick. A doc unstamped on either side counts as changed: being in a delta signals change.
 */
function partitionByChangeStamp(collection, markers) {
  const changed = [];
  const reused = [];
  markers.forEach((marker) => {
    const existing = collection.getItem(marker.id);
    const existingMillis = changedAtMillis(existing);
    const incomingMillis = changedAtMillis(marker);
    if (existingMillis !== null && incomingMillis !== null && incomingMillis <= existingMillis) {
      reused.push(existing);
    } else {
      changed.push(marker);
    }
  });
  return {changed, reused};
}

/**
 * Applies a polled competitiveBuildings response to it and its derived comps collection.
 * @return {boolean} See applyMarkersResponse.
 */
function applyCompetitiveIndustrialResponse(response, context) {
  const {markers: slimBuildings, mode, deletedIds = []} = response;
  const buildingCollection = getFilterCollection('competitiveBuildings');
  const compCollection = getFilterCollection('competitiveBuildingComps');
  if (mode === 'full') {
    const {buildings, comps} = decorateCompetitiveIndustrial(slimBuildings, context);
    buildingCollection.replaceItems(...buildings, ...localOnlyItems(buildingCollection));
    compCollection.replaceItems(...comps, ...localOnlyItems(compCollection));
    return true;
  }
  const {changed, reused} = partitionByChangeStamp(buildingCollection, slimBuildings);
  const {buildings: decoratedBuildings, comps: freshComps} = decorateCompetitiveIndustrial(changed, context);
  const buildings = [...decoratedBuildings, ...reused];
  // Old comp ids may no longer exist, so every comp of a changed or deleted parent is dropped
  // before the re-derived comps are added.
  const parentIds = new Set([...changed.map((building) => building.id), ...deletedIds]);
  const staleComps = compCollection.getSource().filter((comp) => parentIds.has(comp.parent?.id));
  if (staleComps.length) {
    compCollection.removeItems(...staleComps);
  }
  if (buildings.length) {
    buildingCollection.addItems(...buildings);
  }
  if (freshComps.length) {
    compCollection.addItems(...freshComps);
  }
  if (deletedIds.length) {
    buildingCollection.removeItems(...deletedIds.map((id) => ({id})));
  }
  return slimBuildings.length > 0 || deletedIds.length > 0;
}

/**
 * Applies one polled `/markers` response to the local filter collection. Markers the decorator
 * deems ineligible (e.g. missing location data) may have just become ineligible, so a delta
 * removes them; in full mode they are simply absent from the new set.
 *
 * A full response is not expected mid-session — the backend answers every valid `since` with a
 * delta — but it stays part of the contract, so it is handled as a wholesale rebuild: decorate
 * everything and replace, carrying forward only local-only items.
 * @return {boolean} Whether a backend-filter refresh is warranted — always true for full mode,
 *   which carries no change information.
 */
function applyMarkersResponse(filterCollection, response, context) {
  if (filterCollection === 'competitiveBuildings') {
    return applyCompetitiveIndustrialResponse(response, context);
  }
  const {markers, mode, deletedIds = []} = response;
  const collection = getFilterCollection(filterCollection);
  if (mode === 'full') {
    const items = slimMarkerDecorators[filterCollection](markers, context);
    collection.replaceItems(...items, ...localOnlyItems(collection));
    return true;
  }
  const {changed, reused} = partitionByChangeStamp(collection, markers);
  const items = [...slimMarkerDecorators[filterCollection](changed, context), ...reused];
  const eligibleIds = new Set(items.map((item) => item.id));
  const ineligible = markers.filter((marker) => !eligibleIds.has(marker.id)).map(({id}) => ({id}));
  if (items.length) {
    collection.addItems(...items);
  }
  const removals = [...ineligible, ...deletedIds.map((id) => ({id}))];
  if (removals.length) {
    collection.removeItems(...removals);
  }
  return markers.length > 0 || deletedIds.length > 0;
}

const state = Object.assign(baseState, {
  collectionNames,
  ...collectionNames.reduce((acc, name) => {
    acc[name] = [];
    return acc;
  }, {}),
});

const getters = Object.assign(baseGetters, {});

const mutations = Object.assign(baseMutations, {});

/**
 * Initial Full Response through the poll's apply path, plus the Watermark that makes the collection
 * pollable. Throws so the caller can report.
 */
async function loadSlimCollection(filterCollection, context) {
  const response = await FilterService.fetchSlimMarkers(filterCollection);
  applyMarkersResponse(filterCollection, response, context);
  markerWatermarks.set(filterCollection, response.asOf);
  const published = filterCollection === 'competitiveBuildings'
    ? ['competitiveBuildings', 'competitiveBuildingComps']
    : [filterCollection];
  published.forEach((name) => {
    context.commit('updateCollection', {name, value: getFilterCollection(name).getFiltered()});
  });
}

/** Store action loading one marker collection; a failure is logged and toasted as `failureMessage`. */
const markerLoader = (filterCollection, failureMessage) => async (context) => {
  try {
    await loadSlimCollection(filterCollection, context);
  } catch (error) {
    console.error(wrapError(error, failureMessage));
    context.dispatch('addErrorMessage', failureMessage, {root: true});
  }
};

const actions = Object.assign(baseActions, {
  loadNorthPointMultiFamilyProjects: markerLoader('multiFamilyProjects', 'Failed to retrieve NorthPoint Multi-Family Projects'),
  loadNorthPointIndustrialProjects: markerLoader('industrialProjects', 'Failed to retrieve NorthPoint Industrial Projects'),
  loadMultiFamilyAcquisitionProjects: markerLoader('multiFamilyAcquisitionProjects', 'Failed to retrieve NorthPoint Multi-Family Acquisition Projects'),
  loadNorthPointDataCenterProjects: markerLoader('dataCenterProjects', 'Failed to retrieve NorthPoint Data Center Projects'),
  loadNorthPointAcquisitionProjects: markerLoader('acquisitionProjects', 'Failed to retrieve NorthPoint Acquisition Projects'),
  loadNorthPointIndustrialBuildings: markerLoader('industrialBuildings', 'Failed to load NorthPoint Industrial Buildings'),
  loadCompetitiveIndustrial: markerLoader('competitiveBuildings', 'Failed to load competitive data'),
  loadCompetitiveMultifamily: markerLoader('multiFamilyBuildings', 'Failed to load competitive data'),
  loadCompetitiveDataCenter: markerLoader('dataCenterBuildings', 'Failed to load competitive data'),
  loadCompetitivePortfolios: markerLoader('competitivePortfolios', 'Failed to load competitive portfolios'),
  /**
   * @param {ActionContext} context
   */
  async loadMondayItems({commit, dispatch}) {
    try {
      const filterCollection = 'mondayItems';
      const items = await queryCollection('beyondMaps-monday-items', {}, true);
      getFilterCollection(filterCollection).addItems(...items.filter((item) => item.deleted == null));
      commit('updateCollection', {
        name: filterCollection,
        value: getFilterCollection(filterCollection).getFiltered(),
      });
    } catch (error) {
      console.error(wrapError(error, 'Failed to load monday items'));
      dispatch('addErrorMessage', 'Failed to load monday board items for northpoint buildings', {root: true});
    }
  },
  /**
   * Polls `/markers` for every collection that recorded a watermark, in parallel, applying the
   * responses locally. Watermarks advance only on success, so a failed collection retries its
   * window on the next tick.
   * @param {ActionContext} context
   */
  async pollMarkerUpdates({dispatch, getters}) {
    if (markerWatermarks.size === 0) return;
    if (markerPollStartedAt && Date.now() - markerPollStartedAt < MARKER_POLL_ABANDON_MS) return;
    markerPollStartedAt = Date.now();
    const tick = ++markerPollTick;
    try {
      const context = {dispatch, getters};
      const results = await Promise.all([...markerWatermarks.keys()].map(async (filterCollection) => {
        try {
          const since = markerWatermarks.get(filterCollection);
          const response = await FilterService.fetchSlimMarkers(filterCollection, since);
          if (tick !== markerPollTick) return false;
          const changed = applyMarkersResponse(filterCollection, response, context);
          markerWatermarks.set(filterCollection, response.asOf);
          return changed;
        } catch (error) {
          if (tick !== markerPollTick) return false;
          console.error(wrapError(error, `Marker update poll failed for '${filterCollection}'`));
          return false;
        }
      }));
      // Polled markers are unknown to the active backend filter ID sets until they are refreshed.
      if (tick === markerPollTick && results.some(Boolean)) {
        await dispatch('filters/refreshBackendFilters', undefined, {root: true});
      }
    } finally {
      if (tick === markerPollTick) {
        markerPollStartedAt = 0;
      }
    }
  },
  /**
   * Applies markers this session wrote as a poll would, decorated and with derived comps, so the map
   * shows the write before the next tick. The watermark is left alone; that tick reuses these items.
   * @param {ActionContext} context
   * @param {{filterCollection: string, markers?: object[], deletedIds?: string[]}} changes
   * @return {boolean} Whether anything changed.
   */
  applyMarkerChanges({dispatch, getters}, {filterCollection, markers = [], deletedIds = []}) {
    // A collection the map hasn't loaded shows nothing to update
    if (!getFilterCollection(filterCollection)) return false;
    return applyMarkersResponse(filterCollection, {mode: 'delta', markers, deletedIds}, {dispatch, getters});
  },
  /**
   * Loads data into all collections in the store.
   * @param {ActionContext} context
   */
  async loadCollections({dispatch}) {
    await dispatch('subscribeCollections');
    const actions = [
      'loadNorthPointIndustrialBuildings',
      'loadMondayItems',
      'loadNorthPointIndustrialProjects',
      'loadNorthPointAcquisitionProjects',
      'loadNorthPointDataCenterProjects',
      'loadCompetitiveIndustrial',
      'loadNorthPointMultiFamilyProjects',
      'loadMultiFamilyAcquisitionProjects',
      'loadCompetitiveMultifamily',
      'loadCompetitiveDataCenter',
      'loadCompetitivePortfolios',
    ];
    // The loaders are independent. The old serial loop guarded against main-thread contention that
    // measurement never found; in parallel all markers land ~2s sooner. Collections appear in response order.
    await Promise.all(actions.map((action) => dispatch(action)));
  },
});

export default {
  namespaced: true,
  state: () => state,
  mutations,
  actions,
  getters,
};
